import { useCallback, useEffect, useRef, useState } from "react";
import { normalizeError } from "api/helpers";
import { ProcessBase, ProcessState } from "types/process";

export interface UseProcessPollingOptions<
  TStartResponse,
  TProcess extends ProcessBase
> {
  /**
   * Kickoff API call. Returns a "start response".
   * Often that IS the process, but you can map via getStartProcess.
   */
  startRequest: () => Promise<TStartResponse>;

  /**
   * Poll API call. Given processId, returns the latest process snapshot.
   */
  pollRequest: (processId: string) => Promise<TProcess>;

  /**
   * If startRequest doesn't return the process directly, supply this mapper.
   * Default assumes startRequest returns the process.
   */
  getStartProcess?: (startResponse: TStartResponse) => TProcess;

  /**
   * Poll interval in ms. Default 2000.
   */
  pollIntervalMs?: number;

  /**
   * Optional hooks
   */
  onProcessUpdate?: (process: TProcess) => void;
  onTerminal?: (process: TProcess) => void;

  /**
   * If true, the hook will immediately poll once after start (useful if kickoff returns INITIALIZING).
   * Default: false (because kickoff usually already returns the first snapshot).
   */
  pollOnceImmediatelyAfterStart?: boolean;
}

export interface UseProcessPollingResult<TProcess extends ProcessBase> {
  process: TProcess | null;

  processId: string | null;
  processState: ProcessState;
  message: string;
  percentComplete?: number;

  isIdle: boolean;
  isStarting: boolean;
  isPolling: boolean;
  isBusy: boolean;
  isTerminal: boolean;

  error: unknown | null;

  start: () => Promise<TProcess | null>;
  reset: () => void;
  pollOnce: () => Promise<TProcess | null>;
}

const POLL_PROCESSSTATES: readonly ProcessState[] = [
  ProcessState.QUEUED,
  ProcessState.PROCESSING,
];

const TERMINAL_PROCESSSTATES: readonly ProcessState[] = [
  ProcessState.COMPLETE,
  ProcessState.ERROR,
];

export function useProcessPolling<TStartResponse, TProcess extends ProcessBase>(
  options: UseProcessPollingOptions<TStartResponse, TProcess>
): UseProcessPollingResult<TProcess> {
  const {
    startRequest,
    pollRequest,
    getStartProcess,
    pollIntervalMs = 2000,
    onProcessUpdate,
    onTerminal,
    pollOnceImmediatelyAfterStart = false,
  } = options;

  const [process, setProcess] = useState<TProcess | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<unknown | null>(null);

  const waitingForPollResponse = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const safeSetProcess = useCallback((process: TProcess | null) => {
    if (!isMountedRef.current) return;
    setProcess(process);
  }, []);

  const safeSetError = useCallback((error: unknown | null) => {
    if (!isMountedRef.current) return;
    setError(error);
  }, []);

  const processId = process?.id ?? null;

  const shouldPoll = useCallback(
    (process: TProcess) => POLL_PROCESSSTATES.includes(process.processState),
    []
  );

  const isTerminalState = useCallback(
    (process: TProcess) =>
      TERMINAL_PROCESSSTATES.includes(process.processState),
    []
  );

  const updateProcess = useCallback(
    (process: TProcess) => {
      safeSetProcess(process); //Save the process to state
      onProcessUpdate?.(process); //Run callback (if supplied) for a process state change

      //If a finishing callback is supplied and the process has terminated, run it.
      if (!shouldPoll(process) && isTerminalState(process)) {
        onTerminal?.(process);
      }
    },
    [isTerminalState, onProcessUpdate, onTerminal, safeSetProcess, shouldPoll]
  );

  const pollOnce = useCallback(async (): Promise<TProcess | null> => {
    if (!processId) return null;
    if (waitingForPollResponse.current) return process;

    waitingForPollResponse.current = true; //prevent sending another poll if waiting for prior one
    try {
      const latestProcess = await pollRequest(processId);
      if (!isMountedRef.current) return null;

      updateProcess(latestProcess);
      return latestProcess;
    } catch (e) {
      safeSetError(normalizeError(e));
      return null;
    } finally {
      waitingForPollResponse.current = false;
    }
  }, [pollRequest, process, processId, safeSetError, updateProcess]);

  const start = useCallback(async (): Promise<TProcess | null> => {
    if (isStarting) return process;

    safeSetError(null);
    setIsStarting(true);

    try {
      const resp = await startRequest(); //Callback to initiate the process
      if (!isMountedRef.current) return null;

      const startProc = getStartProcess
        ? getStartProcess(resp)
        : (resp as unknown as TProcess);

      updateProcess(startProc);

      if (pollOnceImmediatelyAfterStart && shouldPoll(startProc)) {
        void pollOnce();
      }

      return startProc;
    } catch (e) {
      safeSetError(normalizeError(e));
      return null;
    } finally {
      if (isMountedRef.current) setIsStarting(false);
    }
  }, [
    getStartProcess,
    isStarting,
    pollOnce,
    pollOnceImmediatelyAfterStart,
    process,
    safeSetError,
    shouldPoll,
    startRequest,
    updateProcess,
  ]);

  const reset = useCallback(() => {
    waitingForPollResponse.current = false;
    if (isMountedRef.current) {
      setIsPolling(false);
      setIsStarting(false);
    }
    safeSetError(null);
    safeSetProcess(null);
  }, [safeSetError, safeSetProcess]);

  // Interval polling effect
  useEffect(() => {
    if (!process || !processId) {
      setIsPolling(false);
      return;
    }

    if (!shouldPoll(process)) {
      setIsPolling(false);
      return;
    }

    setIsPolling(true);

    const intervalId = window.setInterval(() => {
      void pollOnce();
    }, pollIntervalMs);

    return () => {
      window.clearInterval(intervalId);
      if (isMountedRef.current) setIsPolling(false);
    };
  }, [pollIntervalMs, pollOnce, process, processId, shouldPoll]);

  const processState = process?.processState ?? ProcessState.IDLE;
  const message = process?.message ?? "";
  const percentComplete = process?.percentComplete;

  const isIdle = process === null;
  const isBusy =
    process !== null &&
    (processState === ProcessState.INITIALIZING || shouldPoll(process));

  const isTerminal = process !== null && isTerminalState(process);

  return {
    process,
    processId,
    processState,
    message,
    percentComplete,

    isIdle,
    isStarting,
    isPolling,
    isBusy,
    isTerminal,

    error,

    start,
    reset,
    pollOnce,
  };
}
